/**
 * Defaulters.jsx — Phase 23B — Operational overdue defaulters report.
 *
 * Two modes:
 *  - "overdue" (default): only counts instalments whose due-month <= today.
 *    Surfaces ageing buckets (0-30 / 31-60 / 61-90 / 90+), oldest unpaid
 *    month, count of unpaid months — the real-world cashier view.
 *  - "session": legacy session-total view (expected_all − paid). Useful
 *    for end-of-year reconciliation.
 *
 * Backend: GET /api/school/reports/defaulters?mode=overdue|session
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Warning,
  WhatsappLogo,
  DownloadSimple,
  Printer,
  CurrencyInr,
  Hourglass,
  Users as UsersIcon,
  Calendar,
  Clock,
} from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const MONTH_LABEL = (ym) => {
  if (!ym || typeof ym !== "string" || ym.length < 7) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const BUCKET_TONE = {
  "0-30": "bg-amber-100 text-amber-800 border-amber-200",
  "31-60": "bg-orange-100 text-orange-800 border-orange-200",
  "61-90": "bg-rose-100 text-rose-800 border-rose-200",
  "90+": "bg-red-100 text-red-900 border-red-300",
};

const buildQuery = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== "__all__") usp.set(k, v);
  });
  return usp.toString();
};

async function downloadCsv(url, filename) {
  try {
    const token = tokenStore.get();
    const res = await fetch(`${API_BASE}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(obj);
  } catch (e) {
    toast.error(`Download failed: ${e.message}`);
  }
}

function buildWhatsappUrl({ school, student, pending, upiId, oldestUnpaid, mode }) {
  const phone = (student.parent_phone || "").replace(/[^\d]/g, "");
  if (!phone) return null;
  const upiLine = upiId ? `\nUPI: ${upiId}` : "";
  const monthLine = mode === "overdue" && oldestUnpaid
    ? `\nOverdue since: ${MONTH_LABEL(oldestUnpaid)}`
    : "";
  const text = encodeURIComponent(
    `Dear Parent,\n\n` +
    `This is a fee reminder from ${school.school_name}.\n` +
    `Student: ${student.name} (Class ${student.class_name})\n` +
    `Pending: ${formatINR(pending)}${monthLine}\n\n` +
    `Kindly clear the pending fee at the earliest.${upiLine}\n` +
    `Thank you.`
  );
  const e164 = phone.length === 10 ? `91${phone}` : phone;
  return `https://wa.me/${e164}?text=${text}`;
}

const PAGE_SIZE = 50;

export default function Defaulters() {
  const [school, setSchool] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("__all__");
  const [minPending, setMinPending] = useState("");
  const [mode, setMode] = useState("overdue");
  const [page, setPage] = useState(0);

  const [data, setData] = useState({
    total: 0, items: [],
    current_month: null,
    mode: "overdue",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const filter = useMemo(() => ({
    session_id: sessionId || undefined,
    class_id: classId !== "__all__" ? classId : undefined,
    min_pending: minPending || undefined,
    mode,
  }), [sessionId, classId, minPending, mode]);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError("");
    try {
      const q = buildQuery({ ...filter, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      const { data: d } = await api.get(`/school/reports/defaulters?${q}`);
      // Phase 25C — DO NOT spread a default `ageing_buckets`/totals here:
      // when the backend strips them for the cashier role, having a default
      // would make `moneyTotalsHidden` always false and leak ₹0 placeholders
      // in the KPI strip + ageing buckets row.
      setData(d);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filter, page, sessionId]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: ss }, { data: cs }] = await Promise.all([
          api.get("/school/me"),
          api.get("/school/sessions"),
          api.get("/school/classes"),
        ]);
        setSchool(s);
        setSessions(ss);
        setClasses(cs);
        const active = ss.find((x) => x.is_active);
        if (active) setSessionId(active.id);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => { setPage(0); }, [sessionId, classId, minPending, mode]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const filteredClasses = classes.filter((c) => !sessionId || c.session_id === sessionId);
  const isOverdue = mode === "overdue";

  const sendWhatsapp = (row) => {
    if (!row.parent_phone) {
      toast.error("No parent phone on record. Edit student to add one.");
      return;
    }
    const url = buildWhatsappUrl({
      school: school || { school_name: "School" },
      student: row,
      pending: isOverdue ? row.overdue : row.pending,
      upiId: school?.upi_id,
      oldestUnpaid: row.oldest_unpaid_month,
      mode,
    });
    if (!url) { toast.error("Invalid phone"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const exportCsv = () => {
    const q = buildQuery(filter);
    downloadCsv(`/school/reports/exports/defaulters.csv?${q}`, `defaulters-${mode}-${Date.now()}.csv`);
  };

  // Derived KPI values (mode-aware)
  const kpiPending = isOverdue ? (data.overdue_total ?? data.pending_total ?? 0) : (data.pending_total ?? 0);
  const kpiExpected = isOverdue ? (data.expected_to_date ?? data.expected_total ?? 0) : (data.expected_total ?? 0);
  const buckets = data.ageing_buckets || { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
  // Phase 25C — backend strips school-wide ₹ totals + ageing_buckets for
  // cashier role. Detect that via missing money keys and hide the
  // school-wide aggregate cards. Per-student rows remain.
  const moneyTotalsHidden =
    data.overdue_total === undefined &&
    data.pending_total === undefined &&
    data.expected_total === undefined &&
    data.ageing_buckets === undefined;

  return (
    <div className="space-y-6 print:space-y-3" data-testid="defaulters-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5">
            <Warning size={12} weight="duotone" /> Defaulters
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            {isOverdue ? "Overdue dues" : "Pending fee report"}
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            {isOverdue ? (
              <>Active students with <span className="font-semibold text-slate-700">unpaid past-due instalments</span>.
                Future months are not counted as overdue. Sorted by oldest unpaid month first.</>
            ) : (
              <>Active students whose paid amount is less than the expected fee for the session.
                Sorted by largest pending first.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1.5" data-testid="defaulters-csv-btn">
            <DownloadSimple size={14} /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5" data-testid="defaulters-print-btn">
            <Printer size={14} /> Print
          </Button>
        </div>
      </div>

      {/* Mode segmented control */}
      <div
        className="inline-flex p-1 rounded-lg border border-slate-200 bg-slate-50 print:hidden"
        role="tablist"
        aria-label="Defaulters mode"
        data-testid="defaulters-mode-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={isOverdue}
          onClick={() => setMode("overdue")}
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            isOverdue ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "text-slate-600 hover:text-slate-900"
          }`}
          data-testid="defaulters-mode-overdue"
        >
          Overdue
          <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-600 font-bold">live</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isOverdue}
          onClick={() => setMode("session")}
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            !isOverdue ? "bg-white text-slate-900 shadow-sm border border-slate-200" : "text-slate-600 hover:text-slate-900"
          }`}
          data-testid="defaulters-mode-session"
        >
          Session total
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 border border-slate-200 bg-white rounded-lg print:hidden" data-testid="defaulters-filters">
        <div>
          <Label className="text-xs">Session</Label>
          <Select value={sessionId} onValueChange={setSessionId}>
            <SelectTrigger className="mt-1" data-testid="defaulters-session"><SelectValue placeholder="Session" /></SelectTrigger>
            <SelectContent>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.is_active ? " · Active" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Class</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="mt-1" data-testid="defaulters-class"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Min {isOverdue ? "overdue" : "pending"} (₹)</Label>
          <Input
            type="number" min="0" placeholder="0"
            value={minPending}
            onChange={(e) => setMinPending(e.target.value)}
            className="mt-1"
            data-testid="defaulters-min-pending"
          />
        </div>
        <div className="flex items-end">
          <div className="text-xs text-slate-500">
            Showing <span className="font-semibold text-slate-900">{data.items.length}</span> of <span className="font-semibold text-slate-900">{data.total}</span>
            {isOverdue && data.current_month && (
              <div className="text-[10px] text-slate-400 mt-0.5">As of {MONTH_LABEL(data.current_month)}</div>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards — hidden in cashier mode (school-wide ₹ totals
          stripped). Defaulters count remains. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="defaulters-kpis">
        <Kpi icon={UsersIcon} label="Defaulters" value={data.total} tone="amber" testid="kpi-defaulters" />
        {!moneyTotalsHidden && (
          <>
            <Kpi
              icon={CurrencyInr}
              label={isOverdue ? "Overdue total" : "Pending total"}
              hint="Σ per-student (expected − raw_paid), each row clamped at ₹0. Differs from the Reports page 'Pending (overall)' KPI when any student has over-paid (Reports subtracts capped collected, this clamps each student individually)."
              value={formatINR(kpiPending)}
              tone="rose"
              testid="kpi-pending-total"
            />
            <Kpi
              icon={Hourglass}
              label={isOverdue ? "Expected to date" : "Expected total"}
              hint="Total liability of students who still owe money."
              value={formatINR(kpiExpected)}
              tone="slate"
              testid="kpi-expected"
            />
          </>
        )}
      </div>

      {/* Ageing buckets — overdue mode only, hidden in cashier mode */}
      {isOverdue && !moneyTotalsHidden && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="defaulters-ageing">
          <BucketCard label="0–30 days" count={buckets["0-30"]} tone="amber" testid="bucket-0-30" />
          <BucketCard label="31–60 days" count={buckets["31-60"]} tone="orange" testid="bucket-31-60" />
          <BucketCard label="61–90 days" count={buckets["61-90"]} tone="rose" testid="bucket-61-90" />
          <BucketCard label="90+ days" count={buckets["90+"]} tone="red" testid="bucket-90-plus" />
        </div>
      )}

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>}

      {/* Table — desktop */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="defaulters-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Student</th>
                <th className="text-left font-semibold px-4 py-3">Class</th>
                {isOverdue && <th className="text-left font-semibold px-4 py-3">Overdue since</th>}
                {isOverdue && <th className="text-right font-semibold px-4 py-3"># Months</th>}
                <th className="text-right font-semibold px-4 py-3">{isOverdue ? "Expected (to date)" : "Expected"}</th>
                <th className="text-right font-semibold px-4 py-3">Paid</th>
                <th className="text-right font-semibold px-4 py-3">{isOverdue ? "Overdue" : "Pending"}</th>
                {isOverdue ? (
                  <th className="text-left font-semibold px-4 py-3">Ageing</th>
                ) : (
                  <th className="text-right font-semibold px-4 py-3">Days</th>
                )}
                <th className="text-right font-semibold px-4 py-3 print:hidden">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && data.items.length === 0 ? (
                <tr><td colSpan={isOverdue ? 9 : 7} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={isOverdue ? 9 : 7} className="px-4 py-10 text-center text-slate-400" data-testid="defaulters-empty">
                  {isOverdue ? "No overdue students in this filter. 🎉" : "No defaulters in this filter. 🎉"}
                </td></tr>
              ) : data.items.map((r) => {
                const pending = isOverdue ? r.overdue : r.pending;
                const expected = isOverdue ? r.expected_to_date : r.expected;
                return (
                  <tr key={r.student_id} className="hover:bg-slate-50" data-testid={`defaulters-row-${r.student_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.admission_no}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.class_name}</td>
                    {isOverdue && (
                      <td className="px-4 py-3 text-slate-700 text-xs">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} className="text-slate-400" />
                          {MONTH_LABEL(r.oldest_unpaid_month)}
                        </div>
                      </td>
                    )}
                    {isOverdue && (
                      <td className="px-4 py-3 text-right font-mono text-slate-700 text-xs">{r.overdue_months_count}</td>
                    )}
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{formatINR(expected)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatINR(r.paid)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono font-semibold text-rose-700">{formatINR(pending)}</span>
                      {!isOverdue && (
                        <span className={`ml-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${r.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
                          {r.status}
                        </span>
                      )}
                    </td>
                    {isOverdue ? (
                      <td className="px-4 py-3">
                        <span
                          className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${BUCKET_TONE[r.ageing_bucket] || BUCKET_TONE["0-30"]}`}
                          title={`${r.days_overdue} days overdue`}
                          data-testid={`bucket-pill-${r.student_id}`}
                        >
                          {r.ageing_bucket}
                        </span>
                      </td>
                    ) : (
                      <td className="px-4 py-3 text-right font-mono text-slate-600 text-xs">{r.days_overdue}</td>
                    )}
                    <td className="px-4 py-3 text-right print:hidden">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendWhatsapp(r)}
                        disabled={!r.parent_phone}
                        title={r.parent_phone ? "Send WhatsApp reminder" : "No parent phone"}
                        className="gap-1 h-8 text-xs"
                        data-testid={`defaulters-wa-${r.student_id}`}
                      >
                        <WhatsappLogo size={14} /> Remind
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cards — mobile */}
      <div className="md:hidden space-y-2" data-testid="defaulters-cards">
        {data.items.length === 0 && !loading ? (
          <div className="p-6 text-center text-slate-400 border border-slate-200 bg-white rounded-lg">
            {isOverdue ? "No overdue students. 🎉" : "No defaulters. 🎉"}
          </div>
        ) : data.items.map((r) => {
          const pending = isOverdue ? r.overdue : r.pending;
          const expected = isOverdue ? r.expected_to_date : r.expected;
          return (
            <div key={r.student_id} className="p-3 border border-slate-200 bg-white rounded-lg" data-testid={`defaulters-card-${r.student_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.class_name} · <span className="font-mono">{r.admission_no}</span></div>
                </div>
                {isOverdue ? (
                  <span
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${BUCKET_TONE[r.ageing_bucket] || BUCKET_TONE["0-30"]}`}
                    data-testid={`bucket-pill-mobile-${r.student_id}`}
                  >
                    {r.ageing_bucket}
                  </span>
                ) : (
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${r.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
                    {r.status}
                  </span>
                )}
              </div>
              {isOverdue && (
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar size={11} className="text-slate-400" />
                    Since {MONTH_LABEL(r.oldest_unpaid_month)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={11} className="text-slate-400" />
                    {r.overdue_months_count} {r.overdue_months_count === 1 ? "month" : "months"}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><div className="text-slate-500">{isOverdue ? "Expected" : "Expected"}</div><div className="font-mono text-slate-900">{formatINR(expected)}</div></div>
                <div><div className="text-slate-500">Paid</div><div className="font-mono text-emerald-700">{formatINR(r.paid)}</div></div>
                <div><div className="text-slate-500">{isOverdue ? "Overdue" : "Pending"}</div><div className="font-mono font-semibold text-rose-700">{formatINR(pending)}</div></div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-xs text-slate-500">{r.days_overdue} days overdue</div>
                <Button size="sm" variant="outline" onClick={() => sendWhatsapp(r)} disabled={!r.parent_phone} className="gap-1 h-11 text-xs" data-testid={`defaulters-wa-mobile-${r.student_id}`}>
                  <WhatsappLogo size={14} /> WhatsApp
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between print:hidden" data-testid="defaulters-pagination">
          <div className="text-xs text-slate-500">Page {page + 1} of {totalPages}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="defaulters-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="defaulters-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "slate", testid, hint }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
  };
  return (
    <div className="p-4 border border-slate-200 bg-white rounded-lg flex flex-col gap-2" data-testid={testid}>
      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1">
          <span>{label}</span>
          {hint && (
            <span
              title={hint}
              className="cursor-help text-slate-400 normal-case tracking-normal"
              data-testid={testid ? `${testid}-hint` : undefined}
            >
              ⓘ
            </span>
          )}
        </div>
        <div className="text-2xl font-bold text-slate-900 font-heading mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function BucketCard({ label, count, tone, testid }) {
  const tones = {
    amber: { border: "border-amber-200", bar: "bg-amber-400", text: "text-amber-900", bg: "bg-amber-50" },
    orange: { border: "border-orange-200", bar: "bg-orange-500", text: "text-orange-900", bg: "bg-orange-50" },
    rose: { border: "border-rose-200", bar: "bg-rose-500", text: "text-rose-900", bg: "bg-rose-50" },
    red: { border: "border-red-300", bar: "bg-red-600", text: "text-red-900", bg: "bg-red-50" },
  }[tone] || { border: "border-slate-200", bar: "bg-slate-400", text: "text-slate-900", bg: "bg-slate-50" };
  return (
    <div className={`relative p-3 border ${tones.border} ${tones.bg} rounded-lg overflow-hidden`} data-testid={testid}>
      <div className={`absolute top-0 left-0 h-1 w-full ${tones.bar}`} />
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-1">{label}</div>
      <div className={`text-2xl font-bold font-heading mt-1 ${tones.text}`}>{count || 0}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{(count || 0) === 1 ? "student" : "students"}</div>
    </div>
  );
}
